import React from 'react';
import { motion } from 'framer-motion';
import { Award, Settings, ChefHat, Rocket, TrendingUp, BadgeCheck } from 'lucide-react';
import { useLang } from '../context/LanguageContext';

export default function FranchiseIncludes() {
  const { t } = useLang();
  const content = t.franchiseIncludes;

  const blocks = [
    {
      title: content.block1.title,
      items: content.block1.items,
      icon: Award,
      gradient: 'from-[#D4AF37]/20 to-[#FFD700]/5',
      iconColor: '#D4AF37',
      border: 'border-[#D4AF37]/30',
    },
    {
      title: content.block2.title,
      items: content.block2.items,
      icon: Settings,
      gradient: 'from-[#3B82F6]/20 to-[#3B82F6]/5',
      iconColor: '#3B82F6',
      border: 'border-[#3B82F6]/30',
    },
    {
      title: content.block3.title,
      items: content.block3.items,
      icon: ChefHat,
      gradient: 'from-[#22C55E]/20 to-[#22C55E]/5',
      iconColor: '#22C55E',
      border: 'border-[#22C55E]/30',
    },
    {
      title: content.block4.title,
      items: content.block4.items,
      icon: Rocket,
      gradient: 'from-[#8B5CF6]/20 to-[#8B5CF6]/5',
      iconColor: '#8B5CF6',
      border: 'border-[#8B5CF6]/30',
    },
  ];

  return (
    <section className="relative py-20 md:py-32 px-6 overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A0B0F] to-transparent opacity-50" />
      <div
        className="absolute top-1/4 left-0 w-96 h-96 rounded-full opacity-10"
        style={{ background: 'radial-gradient(circle, #D4AF37 0%, transparent 70%)' }}
      />
      <div
        className="absolute bottom-1/4 right-0 w-96 h-96 rounded-full opacity-10"
        style={{ background: 'radial-gradient(circle, #8B5CF6 0%, transparent 70%)' }}
      />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-7xl font-black font-heading gold-text mb-6">
            {content.title}
          </h2>
          <p className="text-[#9CA3AF] text-lg md:text-xl max-w-2xl mx-auto">
            {content.subtitle}
          </p>
        </motion.div>

        {/* Price Block - Prominent */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="mb-16 max-w-2xl mx-auto"
        >
          <div
            className="relative overflow-hidden rounded-3xl p-8 md:p-10 text-center"
            style={{
              background: 'linear-gradient(135deg, rgba(212,175,55,0.15) 0%, rgba(255,215,0,0.05) 100%)',
              border: '2px solid rgba(212,175,55,0.4)',
              boxShadow: '0 20px 60px rgba(212,175,55,0.25), inset 0 1px 0 rgba(255,215,0,0.3)',
            }}
          >
            <div className="relative z-10">
              <div className="flex items-center justify-center gap-3 mb-3">
                <BadgeCheck size={28} className="text-[#D4AF37]" />
                <p className="text-[#D4AF37] text-sm md:text-base font-bold uppercase tracking-wider">
                  {content.priceLabel}
                </p>
              </div>
              <p className="text-5xl md:text-6xl font-black font-heading gold-text">
                {content.priceValue}
              </p>
              {content.priceNote && (
                <p className="text-[#9CA3AF] text-sm md:text-base mt-4 leading-relaxed max-w-xl mx-auto">
                  {content.priceNote}
                </p>
              )}
            </div>
            {/* Glow effect */}
            <div
              className="absolute inset-0 opacity-30"
              style={{ background: 'radial-gradient(circle at 50% 50%, rgba(212,175,55,0.3) 0%, transparent 70%)' }}
            />
          </div>
        </motion.div>

        {/* Regular Payments Block */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="mb-16 max-w-4xl mx-auto"
        >
          <h3 className="text-2xl md:text-3xl font-black font-heading text-white mb-8 text-center">
            {content.paymentsTitle}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Royalty */}
            <div
              className="relative overflow-hidden rounded-2xl p-6 md:p-8"
              style={{
                background: 'linear-gradient(135deg, rgba(34,197,94,0.12) 0%, rgba(34,197,94,0.04) 100%)',
                border: '1px solid rgba(34,197,94,0.3)',
                boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
              }}
            >
              <div className="relative z-10">
                <p className="text-[#22C55E] text-sm md:text-base font-bold uppercase tracking-wider mb-4">
                  {content.royalty.label}
                </p>
                <div className="space-y-2">
                  <p className="text-white text-xl md:text-2xl font-bold">
                    {content.royalty.year1}
                  </p>
                  <p className="text-white text-xl md:text-2xl font-bold">
                    {content.royalty.year2plus}
                  </p>
                </div>
              </div>
            </div>

            {/* Marketing Fee */}
            <div
              className="relative overflow-hidden rounded-2xl p-6 md:p-8"
              style={{
                background: 'linear-gradient(135deg, rgba(139,92,246,0.12) 0%, rgba(139,92,246,0.04) 100%)',
                border: '1px solid rgba(139,92,246,0.3)',
                boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
              }}
            >
              <div className="relative z-10">
                <p className="text-[#A78BFA] text-sm md:text-base font-bold uppercase tracking-wider mb-4">
                  {content.marketingFee.label}
                </p>
                <p className="text-white text-3xl md:text-4xl font-black font-heading">
                  {content.marketingFee.value}
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* 4 Blocks Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-16">
          {blocks.map((block, index) => {
            const Icon = block.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.8, delay: 0.1 + index * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className={`relative overflow-hidden rounded-2xl p-8 md:p-10 border ${block.border} hover:scale-[1.02] transition-all duration-500`}
                style={{
                  background: `linear-gradient(135deg, ${block.gradient.replace('from-', 'rgba(').replace('/20', ', 0.2)').replace(' to-', ', rgba(').replace('/5', ', 0.05)')}`,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                }}
              >
                {/* Icon */}
                <div className="flex items-center gap-4 mb-6">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center"
                    style={{
                      background: `linear-gradient(135deg, ${block.iconColor}33 0%, ${block.iconColor}11 100%)`,
                      border: `1px solid ${block.iconColor}44`,
                    }}
                  >
                    <Icon size={26} style={{ color: block.iconColor }} />
                  </div>
                  <h3
                    className="text-2xl md:text-3xl font-black font-heading"
                    style={{ color: block.iconColor }}
                  >
                    {block.title}
                  </h3>
                </div>

                {/* Items list */}
                <ul className="space-y-3">
                  {block.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span
                        className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: block.iconColor }}
                      />
                      <span className="text-[#E5E7EB] text-sm md:text-base leading-relaxed">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>

        {/* Additional Revenue Sources Block */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.8, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative overflow-hidden rounded-3xl p-10 md:p-12"
          style={{
            background: 'linear-gradient(135deg, rgba(107,78,255,0.15) 0%, rgba(139,92,246,0.05) 100%)',
            border: '2px solid rgba(139,92,246,0.3)',
            boxShadow: '0 20px 60px rgba(107,78,255,0.2), inset 0 1px 0 rgba(139,92,246,0.3)',
          }}
        >
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, rgba(139,92,246,0.3) 0%, rgba(139,92,246,0.1) 100%)',
                border: '1px solid rgba(139,92,246,0.4)',
              }}
            >
              <TrendingUp size={30} className="text-[#A78BFA]" />
            </div>
            <h3 className="text-3xl md:text-4xl font-black font-heading purple-text">
              {content.revenueTitle}
            </h3>
          </div>

          {/* Revenue sources grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {content.revenueSources.map((source, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.6 + i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className="flex items-center gap-2 px-4 py-3 rounded-xl"
                style={{
                  background: 'rgba(139,92,246,0.1)',
                  border: '1px solid rgba(139,92,246,0.2)',
                }}
              >
                <span className="w-2 h-2 rounded-full bg-[#A78BFA]" />
                <span className="text-[#E5E7EB] text-sm md:text-base font-medium">
                  {source}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
